import { forwardRef, useRef } from 'react';
import PropTypes from 'prop-types';
import { useSelect } from 'downshift';
import { className, augmentClassName } from '../../../utils/GlobalCssProxy';
import Button from 'react-bootstrap/Button';
import styles from './dropdown-basic.module.scss';

const classNames = augmentClassName(styles);

const SLOTS = {
  item: 'item',
  label: 'label',
  trigger: 'trigger'
};

const DropdownBasic = ({
  children: childrenProp,
  className: classNameProp,
  items,
  onChange,
  triggerModal,
  triggerExternalSiteModal,
  isExternalSite,
  selectedItem,
  loading = false,
  ...props
}) => {
  const {
    getToggleButtonProps,
    getLabelProps,
    getMenuProps,
    getItemProps,
    isOpen,
    highlightedIndex,
    reset // Add reset function here
  } = useSelect({
    items,
    // Open the URL in a new window when an item is selected
    onSelectedItemChange: ({ selectedItem: newSelectedItem }) => {
      //Moved all the behavior from DropdownHeaderDocuments to here;
      if (newSelectedItem?.urlList && newSelectedItem?.urlList?.length > 0) {
        triggerModal(newSelectedItem.urlList);
      } else if (newSelectedItem?.label === 'Prospectus') {
        window.open(newSelectedItem?.url, '_blank', 'location=yes,noopener,noreferrer');
      } else if (isExternalSite) {
        triggerExternalSiteModal();
      } else if (onChange) {
        onChange({ selectedItem: newSelectedItem });
      } else if (newSelectedItem?.url) {
        window.open(newSelectedItem.url, '_blank', 'noopener,noreferrer');
      } else {
        reset();
      }

      setTimeout(() => {
        reset();
      }, 0);
    },
    selectedItem
  });

  const hasItems = items.length > 0;
  const defaultMessage = 'no documents';

  const triggerRef = useRef();

  const classNameRoot = [classNameProp, className.string('dropdown')].filter(Boolean).join(' ');

  const children = Array.isArray(childrenProp) ? childrenProp : [childrenProp].filter(Boolean);

  const itemSlot = children.find(c => c.props.slot === SLOTS.item);
  const triggerSlot = children.find(c => c.props.slot === SLOTS.trigger);

  return (
    <span className={classNameRoot} {...props}>
      <label {...getLabelProps()} aria-label={getLabelProps()?.id} />
      <Button
        ref={triggerRef}
        {...triggerSlot.props}
        {...getToggleButtonProps({}, { suppressRefError: true })}
        aria-label={getLabelProps()?.id}
      />
      <ul
        {...className('dropdown_items', !isOpen && 'd-none')}
        {...getMenuProps({ refKey: triggerRef.current }, { suppressRefError: true })}
      >
        {loading && <LoadingInternal />}
        {!hasItems && !loading && (
          <li {...className('dropdown_item', 'font-weight-bold')}> {defaultMessage}</li>
        )}
        {hasItems &&
          !loading &&
          items.map((item, index) => (
            <DropdownItemInternal
              isHighlighted={index === highlightedIndex}
              isSelected={item === selectedItem}
              key={`${item.label || ''}-${index}`}
              item={item}
              {...itemSlot.props}
              {...getItemProps({
                item,
                index
              })}
            />
          ))}
      </ul>
    </span>
  );
};

DropdownBasic.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  items: PropTypes.array,
  onChange: PropTypes.func,
  triggerModal: PropTypes.func,
  triggerExternalSiteModal: PropTypes.func,
  isExternalSite: PropTypes.bool,
  selectedItem: PropTypes.any,
  loading: PropTypes.bool
};

DropdownBasic.defaultProps = {
  items: []
};

// eslint-disable-next-line react/display-name
const DropdownItemInternal = forwardRef(
  ({ children, className: classNameProp, isHighlighted, isSelected, item, ...props }, ref) => {
    const classNameRoot = [
      classNameProp,
      className.string(
        'dropdown_item',
        'font-weight-bold',
        isHighlighted ? 'highlighted' : undefined,
        isSelected ? 'selected' : undefined
      )
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <li className={classNameRoot} {...props} ref={ref}>
        {children(item)}
      </li>
    );
  }
);

DropdownItemInternal.propTypes = {
  children: PropTypes.func,
  className: PropTypes.string,
  isHighlighted: PropTypes.bool,
  isSelected: PropTypes.bool,
  item: PropTypes.any
};

// eslint-disable-next-line react/display-name
const LoadingInternal = ({ ...props }) => {
  return (
    <li {...classNames('loader')} role="status" {...props}>
      <span {...classNames('sr-only')}></span>
    </li>
  );
};

LoadingInternal.propTypes = {};

const LabelSlot = () => {}; // eslint-disable-line no-empty-function

LabelSlot.defaultProps = {
  slot: SLOTS.label
};

DropdownBasic.Label = LabelSlot;

const TriggerItemSlot = () => {}; // eslint-disable-line no-empty-function

TriggerItemSlot.defaultProps = {
  slot: SLOTS.trigger
};

DropdownBasic.Trigger = TriggerItemSlot;

const DropdownItemSlot = () => {}; // eslint-disable-line no-empty-function

DropdownItemSlot.defaultProps = {
  slot: SLOTS.item
};

DropdownBasic.Item = DropdownItemSlot;

export default DropdownBasic;
